import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowLeft, ArrowRight, Calendar, User } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

const API_BASE_URL = 'https://smartsolutionslk.com/api/api.php';
const IMAGE_BASE_URL = 'https://smartsolutionslk.com/uploads/';

// Animation variants
const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.07 } },
};
const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

const BlogPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const activeTag = searchParams.get('tag') || 'All';

    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Fetch blog posts from API
    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}?resource=blogs`);
                const data = await res.json();
                setBlogPosts(data);
            } catch (err) {
                console.error('Failed to fetch blog posts:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    const getImageSrc = (url) => {
        if (!url) return 'https://placehold.co/1200x800/1f2937/FFFFFF?text=No+Image';
        if (url.startsWith('data:image')) return url;
        if (url.startsWith('http')) return url;
        return `${IMAGE_BASE_URL}${url}`;
    };

    // Build unique tag list from live data
    const availableTags = useMemo(() => {
        const tags = blogPosts
            .flatMap(p => p.tags ? (Array.isArray(p.tags) ? p.tags : p.tags.split(',').map(t => t.trim())) : [])
            .filter(Boolean);
        return ['All', ...new Set(tags)];
    }, [blogPosts]);

    // Filter by tag + search
    const filteredPosts = useMemo(() => {
        return blogPosts.filter(p => {
            const postTags = p.tags
                ? (Array.isArray(p.tags) ? p.tags : p.tags.split(',').map(t => t.trim()))
                : [];
            const matchTag = activeTag === 'All' || postTags.includes(activeTag);
            const matchSearch =
                !searchQuery ||
                p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.author?.toLowerCase().includes(searchQuery.toLowerCase());
            return matchTag && matchSearch;
        });
    }, [blogPosts, activeTag, searchQuery]);

    const handleTagClick = (tag) => {
        if (tag === 'All') {
            setSearchParams({});
        } else {
            setSearchParams({ tag });
        }
        setSearchQuery('');
    };

    // Format date nicely
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric',
            });
        } catch {
            return dateStr;
        }
    };

    return (
        <div className="min-h-screen bg-white dark:bg-black text-slate-900 dark:text-white">

            {/* ── Hero Banner ────────────────────────────────────────────── */}
            <div className="relative pt-36 pb-16 px-6 overflow-hidden bg-slate-50 dark:bg-gray-950">
                {/* Decorative glows */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-32 -left-32 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl" />
                    <div className="absolute -bottom-20 right-0 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
                </div>

                <div className="container mx-auto max-w-5xl relative z-10">
                    {/* Back link */}
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-cyan-500 transition-colors mb-8 group"
                    >
                        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-200" />
                        Back to Home
                    </Link>

                    {/* Page title */}
                    <motion.h1
                        className="text-5xl md:text-7xl font-extrabold tracking-tight mb-4"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
                    >
                        <span className="text-slate-900 dark:text-white">The Smart </span>
                        <span className="text-cyan-400">Solutions Blog</span>
                    </motion.h1>

                    {/* Subtitle */}
                    <motion.p
                        className="text-slate-500 dark:text-gray-400 text-lg md:text-xl max-w-2xl mb-10"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.18, ease: 'easeOut' }}
                    >
                        Insights on technology, design, and innovation from our team.
                    </motion.p>

                    {/* Search bar */}
                    <motion.div
                        className="relative max-w-lg mb-10"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.25 }}
                    >
                        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-500" />
                        <input
                            id="blog-search"
                            type="text"
                            placeholder="Search articles..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-11 pr-5 py-3.5 rounded-xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 focus:border-cyan-500 dark:focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 transition-all duration-300 shadow-sm"
                        />
                    </motion.div>

                    {/* Tag filter pills — only shown when tags exist in data */}
                    {availableTags.length > 1 && (
                        <motion.div
                            className="flex flex-wrap gap-3"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.32 }}
                        >
                            {availableTags.map((tag) => (
                                <motion.button
                                    key={tag}
                                    onClick={() => handleTagClick(tag)}
                                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-250 border ${
                                        activeTag === tag
                                            ? 'bg-cyan-500 text-black border-cyan-500 shadow-md shadow-cyan-500/25'
                                            : 'bg-transparent text-slate-600 dark:text-gray-300 border-slate-300 dark:border-gray-600 hover:border-cyan-500 hover:text-cyan-500 dark:hover:text-cyan-400'
                                    }`}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.96 }}
                                    aria-pressed={activeTag === tag}
                                >
                                    {tag}
                                    {tag === 'All' && (
                                        <span className="ml-2 text-xs opacity-70">({blogPosts.length})</span>
                                    )}
                                </motion.button>
                            ))}
                        </motion.div>
                    )}
                </div>
            </div>

            {/* ── Posts Grid ─────────────────────────────────────────────── */}
            <div className="container mx-auto max-w-7xl px-6 py-16">
                {loading ? (
                    /* Loading skeleton */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="rounded-xl overflow-hidden bg-slate-100 dark:bg-gray-900 animate-pulse">
                                <div className="h-52 bg-slate-200 dark:bg-gray-800" />
                                <div className="p-6 space-y-3">
                                    <div className="h-3 w-32 bg-slate-200 dark:bg-gray-700 rounded" />
                                    <div className="h-5 w-4/5 bg-slate-200 dark:bg-gray-700 rounded" />
                                    <div className="h-4 w-full bg-slate-200 dark:bg-gray-700 rounded" />
                                    <div className="h-4 w-3/4 bg-slate-200 dark:bg-gray-700 rounded" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Result count */}
                        <motion.p
                            key={activeTag + searchQuery}
                            className="text-sm text-slate-400 dark:text-gray-500 mb-8"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            Showing{' '}
                            <span className="text-cyan-500 font-semibold">{filteredPosts.length}</span>{' '}
                            {filteredPosts.length === 1 ? 'article' : 'articles'}
                            {activeTag !== 'All' && ` tagged "${activeTag}"`}
                            {searchQuery && ` matching "${searchQuery}"`}
                        </motion.p>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTag + searchQuery}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                            >
                                {filteredPosts.length > 0 ? (
                                    filteredPosts.map((post, index) => (
                                        <motion.div
                                            key={post.id || index}
                                            variants={cardVariants}
                                            className="group flex flex-col h-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-md hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-300 border border-slate-100 dark:border-gray-800 hover:border-cyan-500/40"
                                            whileHover={{ y: -6 }}
                                            transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                                        >
                                            {/* Image */}
                                            <Link to={`/blog/${post.slug}`} className="block">
                                                <div
                                                    className="relative w-full overflow-hidden"
                                                    style={{ paddingTop: '56.25%' }}
                                                >
                                                    <img
                                                        src={getImageSrc(post.image_url || post.image)}
                                                        alt={post.title}
                                                        className="absolute top-0 left-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'https://placehold.co/1200x800/1f2937/FFFFFF?text=Image+Not+Found';
                                                        }}
                                                    />
                                                    {/* Dark overlay */}
                                                    <div className="absolute inset-0 bg-gradient-to-br from-slate-800/70 via-slate-900/60 to-cyan-900/70 group-hover:opacity-0 transition-opacity duration-500" />
                                                </div>
                                            </Link>

                                            {/* Card body */}
                                            <div className="p-6 flex flex-col flex-grow">
                                                {/* Meta */}
                                                <div className="flex items-center gap-3 text-xs text-slate-400 dark:text-gray-500 mb-3">
                                                    <span className="inline-flex items-center gap-1">
                                                        <Calendar size={12} />
                                                        {formatDate(post.date)}
                                                    </span>
                                                    {post.author && (
                                                        <>
                                                            <span>·</span>
                                                            <span className="inline-flex items-center gap-1">
                                                                <User size={12} />
                                                                {post.author}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>

                                                {/* Title */}
                                                <Link to={`/blog/${post.slug}`}>
                                                    <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors duration-200 leading-snug">
                                                        {post.title}
                                                    </h2>
                                                </Link>

                                                {/* Excerpt */}
                                                {post.excerpt && (
                                                    <p className="text-slate-500 dark:text-gray-400 text-sm leading-relaxed line-clamp-3 flex-grow">
                                                        {post.excerpt}
                                                    </p>
                                                )}

                                                {/* Read more */}
                                                <div className="mt-5 pt-4 border-t border-slate-100 dark:border-gray-800">
                                                    <Link
                                                        to={`/blog/${post.slug}`}
                                                        className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-500 dark:text-cyan-400 hover:text-cyan-600 dark:hover:text-cyan-300 transition-colors group/link"
                                                    >
                                                        Read Article
                                                        <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform duration-200" />
                                                    </Link>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))
                                ) : (
                                    <motion.div
                                        className="col-span-3 flex flex-col items-center justify-center py-28 text-slate-400 dark:text-gray-500"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                    >
                                        <div className="text-6xl mb-4">📭</div>
                                        <p className="text-xl font-semibold mb-2">No articles found</p>
                                        <p className="text-sm">Try a different search term.</p>
                                        <button
                                            onClick={() => { handleTagClick('All'); setSearchQuery(''); }}
                                            className="mt-6 px-6 py-2.5 rounded-full border border-cyan-500 text-cyan-500 font-semibold text-sm hover:bg-cyan-500 hover:text-black transition-all duration-300"
                                        >
                                            Clear Filters
                                        </button>
                                    </motion.div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </>
                )}
            </div>
        </div>
    );
};

export default BlogPage;
